import React, { useState, useEffect } from 'react';
import { FiEdit2, FiSave, FiX, FiPlus } from 'react-icons/fi';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import api from '../../services/api';

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
    ['link'],
    ['clean']
  ]
};

const QUILL_FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'list', 'bullet', 'indent', 'link'
];

const PolicyManagementPage = () => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({ title: '', content: '', icon: 'FiFileText', color: 'blue', is_active: true });

  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    try {
      const response = await api.get('/policies/admin/all');
      setPolicies(response.data);
    } catch (error) {
      console.error('Error fetching policies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (policy) => {
    setEditingId(policy.id);
    setEditForm({
      title: policy.title,
      content: policy.content,
      icon: policy.icon,
      color: policy.color,
      is_active: policy.is_active
    });
  };

  const handleSave = async (id) => {
    try {
      await api.put(`/policies/admin/${id}`, editForm);
      setEditingId(null);
      fetchPolicies();
      alert('Policy updated successfully!');
    } catch (error) {
      console.error('Error updating policy:', error);
      alert('Failed to update policy');
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleCreate = async () => {
    if (!addForm.title.trim()) { alert('Title is required'); return; }
    try {
      await api.post('/policies/admin', addForm);
      setShowAddForm(false);
      setAddForm({ title: '', content: '', icon: 'FiFileText', color: 'blue', is_active: true });
      fetchPolicies();
    } catch (error) {
      console.error('Error creating policy:', error);
      alert('Failed to create policy');
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold mb-6">Office Policy Management</h1>
        <div className="text-center py-8">Loading policies...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Office Policy Management</h1>
          <p className="text-gray-600">Edit and manage company office policies</p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center"
        >
          <FiPlus className="mr-2" size={16} /> Add Policy
        </button>
      </div>

      {/* Add Policy Form */}
      {showAddForm && (
        <div className="bg-white rounded-lg shadow p-6 mb-6 border-l-4 border-blue-500">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-medium text-gray-900">New Policy</h2>
            <div className="flex gap-2">
              <button onClick={handleCreate} className="px-3 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 flex items-center">
                <FiSave className="mr-1" size={16} /> Save
              </button>
              <button onClick={() => setShowAddForm(false)} className="px-3 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 flex items-center">
                <FiX className="mr-1" size={16} /> Cancel
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
              <input type="text" value={addForm.title} onChange={e => setAddForm({...addForm, title: e.target.value})}
                className="w-full border border-gray-300 rounded-md px-3 py-2" placeholder="Policy title" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Icon</label>
              <select value={addForm.icon} onChange={e => setAddForm({...addForm, icon: e.target.value})} className="w-full border border-gray-300 rounded-md px-3 py-2">
                <option value="FiFileText">File Text</option>
                <option value="FiClock">Clock</option>
                <option value="FiCoffee">Coffee</option>
                <option value="FiCalendar">Calendar</option>
                <option value="FiAlertTriangle">Alert Triangle</option>
                <option value="FiPhone">Phone</option>
                <option value="FiLayers">Layers</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
              <select value={addForm.color} onChange={e => setAddForm({...addForm, color: e.target.value})} className="w-full border border-gray-300 rounded-md px-3 py-2">
                <option value="blue">Blue</option>
                <option value="green">Green</option>
                <option value="purple">Purple</option>
                <option value="orange">Orange</option>
                <option value="red">Red</option>
                <option value="yellow">Yellow</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={addForm.is_active} onChange={e => setAddForm({...addForm, is_active: e.target.value === 'true'})} className="w-full border border-gray-300 rounded-md px-3 py-2">
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
            <ReactQuill theme="snow" value={addForm.content} onChange={value => setAddForm({...addForm, content: value})}
              modules={QUILL_MODULES} formats={QUILL_FORMATS} style={{ height: '220px', marginBottom: '42px' }} placeholder="Enter policy content..." />
          </div>
        </div>
      )}

      <div className="space-y-6">
        {policies.map((policy) => (
          <div key={policy.id} className="bg-white rounded-lg shadow p-6">
            {editingId === policy.id ? (
              // Edit Mode
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-medium text-gray-900">Edit Policy</h2>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleSave(policy.id)}
                      className="px-3 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 flex items-center"
                    >
                      <FiSave className="mr-1" size={16} /> Save
                    </button>
                    <button
                      onClick={handleCancel}
                      className="px-3 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 flex items-center"
                    >
                      <FiX className="mr-1" size={16} /> Cancel
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input
                      type="text"
                      value={editForm.title}
                      onChange={(e) => setEditForm({...editForm, title: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Icon</label>
                    <select
                      value={editForm.icon}
                      onChange={(e) => setEditForm({...editForm, icon: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      <option value="FiClock">Clock</option>
                      <option value="FiCoffee">Coffee</option>
                      <option value="FiCalendar">Calendar</option>
                      <option value="FiAlertTriangle">Alert Triangle</option>
                      <option value="FiPhone">Phone</option>
                      <option value="FiLayers">Layers</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                    <select
                      value={editForm.color}
                      onChange={(e) => setEditForm({...editForm, color: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      <option value="blue">Blue</option>
                      <option value="green">Green</option>
                      <option value="purple">Purple</option>
                      <option value="orange">Orange</option>
                      <option value="red">Red</option>
                      <option value="yellow">Yellow</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={editForm.is_active}
                      onChange={(e) => setEditForm({...editForm, is_active: e.target.value === 'true'})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                  <ReactQuill
                    theme="snow"
                    value={editForm.content}
                    onChange={(value) => setEditForm({...editForm, content: value})}
                    modules={QUILL_MODULES}
                    formats={QUILL_FORMATS}
                    style={{ height: '220px', marginBottom: '42px' }}
                    placeholder="Enter policy content..."
                  />
                </div>
              </div>
            ) : (
              // View Mode
              <div>
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center">
                    <h2 className="text-lg font-medium text-gray-900">{policy.title}</h2>
                    <span className={`ml-3 px-2 py-1 text-xs rounded-full ${policy.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {policy.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <button
                    onClick={() => handleEdit(policy)}
                    className="px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center"
                  >
                    <FiEdit2 className="mr-1" size={16} /> Edit
                  </button>
                </div>
                
                <div className="mb-3">
                  <span className="text-sm text-gray-500">Icon: {policy.icon} • Color: {policy.color}</span>
                </div>
                
                <div 
                  className="text-gray-700 prose max-w-none"
                  dangerouslySetInnerHTML={{ __html: policy.content }}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PolicyManagementPage;